import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, FileText, FolderOpen, MapPin, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { fmtDT, fmtD, clientStatusOf } from "@/components/graphisme/gUtils";
import { CREATION_STATUS } from "@/pages/GraphismeCreation";
import { eur } from "@/components/accounting/docUtils";

export default function GraphismeEspaceProjet() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(null); // création ouverte
  const [mode, setMode] = useState(null); // null | "modification"
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [anno, setAnno] = useState(null); // {x, y} en cours
  const [annoText, setAnnoText] = useState("");
  const [busy, setBusy] = useState(false);
  const [warnFile, setWarnFile] = useState(null); // popup « fichier non finalisé »
  const [batChecked, setBatChecked] = useState(false);
  const imgRef = useRef(null);
  const [searchParams] = useSearchParams();

  const load = () => api.get(`/graphisme/espace/projets/${pid}`).then((r) => setP(r.data)).catch(() => navigate("/graphisme/espace"));
  useEffect(() => { load(); }, [pid]);
  // Lien profond : /graphisme/espace/projet/{pid}?creation={cid} ouvre directement la création
  useEffect(() => {
    const q = searchParams.get("creation");
    if (q && p && (p.creations || []).some((x) => x.id === q)) setActive(q);
  }, [p?.id]);

  if (!p) return <div className="min-h-screen bg-neutral-100 flex items-center justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;

  const st = clientStatusOf(p.status);
  const creations = p.creations || [];
  const c = active ? creations.find((x) => x.id === active) : null;
  const latest = c ? (c.versions || []).slice(-1)[0] : null;
  const previewable = (ct) => /^image\/(png|jpe?g|gif|webp)$/.test(ct || "") || ct === "application/pdf";
  // Aperçu : priorité au fichier « Aperçu » de la dernière version, sinon n'importe
  // quel fichier visuel (aperçu puis livraison HD) en remontant les versions
  const versionsDesc = (c?.versions || []).slice().reverse();
  let preview = null, previewVid = null;
  for (const k of ["apercu", "livraison"]) {
    for (const v of versionsDesc) {
      const f = (v.files || []).find((x) => x.kind === k && !x.external && previewable(x.content_type));
      if (f) { preview = f; previewVid = v.id; break; }
    }
    if (preview) break;
  }
  const otherApercus = versionsDesc.flatMap((v) => (v.files || []).filter((f) => !f.external && !previewable(f.content_type)));
  const correctionsExceeded = p.corrections_used >= p.corrections_included;
  // Le client peut valider / demander une modification dès qu'un fichier est visible
  const canAnswer = c && !["validee", "finalisee", "archivee"].includes(c.status)
    && (c.versions || []).some((v) => (v.files || []).length > 0);

  const fileUrl = (fid) => `${API}/graphisme/espace/files/${fid}`;

  const answer = async (action) => {
    if (action === "modification" && !message.trim() && !file) {
      toast.error("Décrivez les modifications souhaitées");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("action", action);
      fd.append("message", message.trim());
      if (file) fd.append("file", file);
      if (c?.bat_mode && action === "valide") fd.append("bat_confirmed", "true");
      await api.post(`/graphisme/espace/creations/${active}/answer`, fd);
      if (action === "valide") toast.success("Création validée — merci !");
      else toast.success("Votre demande de modification a été envoyée");
      setMode(null); setMessage(""); setFile(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const clickPreview = (e) => {
    if (!canAnswer) return;
    const rect = imgRef.current.getBoundingClientRect();
    setAnno({ x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height });
    setAnnoText("");
  };

  const saveAnno = async () => {
    if (!annoText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/graphisme/espace/creations/${active}/annotations`,
        { version_id: latest?.id || "", x: anno.x, y: anno.y, text: annoText.trim() });
      toast.success("Annotation ajoutée");
      setAnno(null); setAnnoText("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="gclient-projet">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <button onClick={() => navigate("/graphisme/espace")} className="text-xs text-neutral-400 hover:text-neutral-900">← Mes projets</button>
          <span className={`text-[11px] rounded-full px-2.5 py-1 border ${st.cls}`}>{st.label}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div>
          <h1 className="font-heading font-bold text-xl">{p.name}</h1>
          <p className="text-xs text-neutral-400 font-mono">{p.number}</p>
        </div>

        {p.planned_slot && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800" data-testid="gclient-planned-slot">
            {p.status === "en_cours" ? (
              <p className="font-semibold">Votre projet est en cours de création.</p>
            ) : (
              <>
                <p className="font-semibold">Votre projet est planifié.</p>
                <p>Créneau de réalisation prévu : {fmtD(p.planned_slot.date)} — {Math.floor(p.planned_slot.start_min / 60)}h{String(p.planned_slot.start_min % 60).padStart(2, "0")} à {Math.floor(p.planned_slot.end_min / 60)}h{String(p.planned_slot.end_min % 60).padStart(2, "0")}</p>
              </>
            )}
          </div>
        )}

        {(p.invoices || []).length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2" data-testid="gclient-invoices">
            <h3 className="font-heading font-bold text-sm">Mes factures</h3>
            {p.invoices.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between gap-2 text-sm flex-wrap" data-testid={`gclient-invoice-${inv.id}`}>
                <span>Facture N° {inv.number}</span>
                <span className="flex items-center gap-2">
                  <span className="font-medium">{eur(inv.total_ttc)}</span>
                  <span className={`text-[11px] rounded-full px-2 py-0.5 border ${inv.status === "payee" ? "bg-green-50 text-green-700 border-green-200" : "bg-orange-50 text-orange-700 border-orange-200"}`}>
                    {inv.status === "payee" ? "Payée" : "À régler"}
                  </span>
                  {inv.pdf_url && <a href={inv.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline" data-testid={`gclient-invoice-pdf-${inv.id}`}>Voir ma facture</a>}
                </span>
              </div>
            ))}
          </div>
        )}

        {!c && (
          <div className="space-y-3" data-testid="gclient-creations">
            {creations.length === 0 && <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">Aucune création pour le moment.</p>}
            {(() => {
              const folders = p.folders || [];
              const creaBtn = (x) => {
                const cs = CREATION_STATUS[x.status] || CREATION_STATUS.a_faire;
                const lv = (x.versions || []).slice(-1)[0];
                return (
                  <button key={x.id} onClick={() => setActive(x.id)} data-testid={`gclient-creation-${x.id}`}
                    className="w-full flex items-center justify-between gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3 hover:border-neutral-400 transition-colors text-left">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{x.name}</p>
                      <p className="text-[11px] text-neutral-400">{lv ? `Version ${lv.label}` : "En préparation"}{x.unlocked ? " · fichiers disponibles" : ""}</p>
                    </div>
                    <span className={`text-[11px] rounded-full px-2.5 py-1 border shrink-0 ${cs.cls}`}>{cs.label}</span>
                  </button>
                );
              };
              const renderFolder = (fd, depth = 0) => {
                const kids = creations.filter((x) => x.folder_id === fd.id);
                const subs = folders.filter((f2) => f2.parent_id === fd.id);
                if (!kids.length && !subs.length) return null;
                return (
                  <div key={fd.id} className={depth ? "ml-4" : ""} data-testid={`gclient-folder-${fd.id}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 flex items-center gap-1.5 mt-2 mb-1.5">
                      <FolderOpen className="w-3.5 h-3.5" /> {fd.name}
                    </p>
                    <div className="space-y-2">{kids.map(creaBtn)}{subs.map((s2) => renderFolder(s2, depth + 1))}</div>
                  </div>
                );
              };
              const ungrouped = creations.filter((x) => !x.folder_id || !folders.some((fd) => fd.id === x.folder_id));
              return (
                <>
                  {folders.filter((fd) => !fd.parent_id).map((fd) => renderFolder(fd))}
                  {ungrouped.map(creaBtn)}
                </>
              );
            })()}
            {p.unlocked_all !== undefined && null}
          </div>
        )}

        {c && (
          <div className="space-y-4" data-testid="gclient-creation-view">
            <button onClick={() => { setActive(null); setMode(null); setAnno(null); }} className="text-xs text-neutral-400 hover:text-neutral-900">← Toutes les créations</button>

            {c.status === "validee" && !c.final_ready && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800" data-testid="gclient-validated-locked">
                <p className="font-semibold">Votre création est validée ✅</p>
                <p className="mt-1">Votre graphiste prépare le fichier final HD — vous pourrez le télécharger ici dès sa mise à disposition.</p>
              </div>
            )}
            {c.final_ready && c.final_available && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800" data-testid="gclient-unlocked">
                <p className="font-semibold">Vos fichiers définitifs sont disponibles.</p>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  {(c.final_files || []).map((f) => (
                    f.external && f.url ? (
                      <a key={f.id} href={f.url} target="_blank" rel="noreferrer" data-testid={`gclient-dl-final-${f.id}`}
                        className="inline-flex items-center gap-1.5 text-xs bg-green-700 text-white rounded-lg px-3 py-2 hover:bg-green-800 transition-colors">
                        <Download className="w-3.5 h-3.5" /> {f.name} (lien externe)
                      </a>
                    ) : (
                      <a key={f.id} href={`${API}/graphisme/espace/creations/${active}/download-final?file_id=${f.id}`}
                        data-testid={`gclient-dl-final-${f.id}`}
                        className="inline-flex items-center gap-1.5 text-xs bg-green-700 text-white rounded-lg px-3 py-2 hover:bg-green-800 transition-colors">
                        <Download className="w-3.5 h-3.5" /> {f.name}
                      </a>
                    )
                  ))}
                  {(c.final_files || []).length > 1 && (
                    <a href={`${API}/graphisme/espace/creations/${active}/download-final`} data-testid="gclient-dl-final-zip"
                      className="inline-flex items-center gap-1.5 text-xs border border-green-300 text-green-800 rounded-lg px-3 py-2 hover:border-green-500 transition-colors">
                      <Download className="w-3.5 h-3.5" /> Tout télécharger (ZIP)
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-bold">{c.name}</h2>
                <span className={`text-[11px] rounded-full px-2.5 py-1 border ${(CREATION_STATUS[c.status] || {}).cls}`}>{(CREATION_STATUS[c.status] || {}).label}</span>
              </div>

              {preview ? (
                <div className="relative select-none" data-testid="gclient-preview-zone">
                  {preview.content_type === "application/pdf" ? (
                    <iframe src={fileUrl(preview.id)} title="Aperçu" className="w-full h-[420px] border border-neutral-200 rounded-lg" data-testid="gclient-preview-pdf" />
                  ) : (
                    <div className="relative inline-block w-full">
                      <img ref={imgRef} src={`${fileUrl(preview.id)}${preview.preview_path ? "?preview=1" : ""}`} alt={c.name} onClick={clickPreview}
                        className={`w-full rounded-lg border border-neutral-100 ${canAnswer ? "cursor-crosshair" : ""}`}
                        data-testid="gclient-preview-img" />
                      {(c.annotations || []).filter((a) => !a.version_id || a.version_id === (previewVid || latest?.id)).map((a, i) => (
                        <div key={a.id} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%` }} data-testid={`gclient-anno-${a.id}`}>
                          <MapPin className={`w-6 h-6 drop-shadow ${a.status === "resolue" ? "text-green-600" : "text-red-500"}`} />
                          <div className="hidden group-hover:block absolute left-6 top-0 bg-white border border-neutral-200 rounded-lg shadow-lg p-2 text-xs w-48 z-10">
                            <p>{a.text}</p>
                            <p className="text-neutral-400 mt-1">{fmtDT(a.at)}</p>
                          </div>
                        </div>
                      ))}
                      {anno && (
                        <div className="absolute -translate-x-1/2 -translate-y-1/2 z-20" style={{ left: `${anno.x * 100}%`, top: `${anno.y * 100}%` }}>
                          <MapPin className="w-6 h-6 text-blue-600 drop-shadow animate-bounce" />
                        </div>
                      )}
                    </div>
                  )}
                  {canAnswer && !c.unlocked && preview.content_type !== "application/pdf" && (
                    <p className="text-[11px] text-neutral-400 mt-1.5">Aperçu client · Cliquez sur l'image pour annoter un point précis.</p>
                  )}
                </div>
              ) : otherApercus.length > 0 ? (
                <div className="space-y-2" data-testid="gclient-preview-files">
                  <p className="text-xs text-neutral-500">Ce format de fichier ne peut pas être prévisualisé dans le navigateur — téléchargez-le pour le consulter :</p>
                  {otherApercus.map((f) => (
                    <div key={f.id} className="flex items-center justify-between gap-3 border border-neutral-200 rounded-xl p-3.5" data-testid={`gclient-file-${f.id}`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <FileText className="w-5 h-5 text-neutral-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{f.name}</p>
                          {f.size ? <p className="text-[11px] text-neutral-400">{(f.size / 1024 / 1024).toFixed(1)} Mo</p> : null}
                        </div>
                      </div>
                      {c.unlocked ? (
                        <a href={fileUrl(f.id)} data-testid={`gclient-file-dl-${f.id}`}
                          className="inline-flex items-center gap-1.5 text-xs bg-neutral-900 text-white rounded-lg px-3 py-2 hover:bg-neutral-800 shrink-0">
                          <Download className="w-3.5 h-3.5" /> Télécharger
                        </a>
                      ) : (
                        <button onClick={() => setWarnFile(f)} data-testid={`gclient-file-dl-${f.id}`}
                          className="inline-flex items-center gap-1.5 text-xs border border-neutral-300 rounded-lg px-3 py-2 hover:border-neutral-500 transition-colors shrink-0">
                          <Download className="w-3.5 h-3.5" /> Télécharger
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-400 text-center py-8">Aucun aperçu disponible pour le moment.</p>
              )}

              {anno && (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2" data-testid="gclient-anno-form">
                  <p className="text-xs font-semibold text-blue-800">Votre commentaire sur ce point :</p>
                  <Textarea rows={2} value={annoText} onChange={(e) => setAnnoText(e.target.value)} placeholder="Ex : Déplacer le logo légèrement plus haut" data-testid="gclient-anno-text" />
                  <div className="flex gap-2">
                    <Button size="sm" disabled={busy || !annoText.trim()} onClick={saveAnno} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="gclient-anno-save">Ajouter l'annotation</Button>
                    <Button size="sm" variant="outline" onClick={() => setAnno(null)}>Annuler</Button>
                  </div>
                </div>
              )}

              {(c.annotations || []).length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid="gclient-anno-list">
                  {(c.annotations || []).map((a) => (
                    <p key={a.id} className="text-xs text-neutral-600 flex items-start gap-1.5">
                      <MapPin className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${a.status === "resolue" ? "text-green-600" : "text-red-400"}`} />
                      <span>{a.text} <span className="text-neutral-400">— {a.status === "resolue" ? "résolue" : a.status === "traitee" ? "traitée" : "ouverte"}</span></span>
                    </p>
                  ))}
                </div>
              )}

              {(() => {
                const latestV = (c.versions || []).slice(-1)[0];
                const vals = (c.validations || []).filter((v) => v.version_id === (latestV?.id || null));
                const required = c.validation_required || 1;
                if (required <= 1 && vals.length === 0) return null;
                return (
                  <div className="mt-4 bg-neutral-50 border border-neutral-200 rounded-xl p-3.5" data-testid="gclient-validations">
                    <p className="text-xs font-semibold text-neutral-600 mb-2">
                      Validations : {Math.min(vals.length, required)} / {required} requises
                    </p>
                    <div className="space-y-1">
                      {vals.map((v, i) => (
                        <p key={i} className="text-xs text-green-700">✓ {v.user_name} — Validé le {v.at?.slice(8, 10)}/{v.at?.slice(5, 7)} à {v.at?.slice(11, 16)}</p>
                      ))}
                      {vals.length < required && <p className="text-xs text-neutral-400">⏳ En attente de {required - vals.length} validation(s)</p>}
                    </div>
                  </div>
                );
              })()}

              {canAnswer && (
                <div className="mt-4 pt-4 border-t border-neutral-100 space-y-3" data-testid="gclient-answer-zone">
                  <p className="text-xs text-neutral-500">Corrections restantes incluses dans votre devis : <strong>{Math.max(0, p.corrections_included - p.corrections_used)}</strong> / {p.corrections_included}</p>
                  {correctionsExceeded && (
                    <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg p-2.5" data-testid="gclient-quota-warning">
                      Le nombre de modifications incluses dans votre devis a été atteint. Cette nouvelle demande peut faire l'objet d'un devis complémentaire.
                    </p>
                  )}
                  {mode !== "modification" ? (
                    <div className="space-y-2">
                      {c.bat_mode && (
                        <label className="flex items-start gap-2 text-xs bg-neutral-50 border border-neutral-200 rounded-lg p-2.5 cursor-pointer" data-testid="gclient-bat-check">
                          <input type="checkbox" checked={batChecked} onChange={(e) => setBatChecked(e.target.checked)} className="mt-0.5" />
                          <span>Je confirme avoir vérifié les textes, coordonnées, dimensions, couleurs et informations présentes sur ce document et valide ce BAT pour production.</span>
                        </label>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <Button onClick={() => answer("valide")} disabled={busy || (c.bat_mode && !batChecked)} className="bg-green-700 hover:bg-green-800 text-white gap-1.5" data-testid="gclient-validate-btn">
                          <CheckCircle2 className="w-4 h-4" /> {c.bat_mode ? "Valider définitivement" : "Valider"}
                        </Button>
                        <Button variant="outline" onClick={() => setMode("modification")} className="gap-1.5" data-testid="gclient-askmodif-btn">
                          <MessageSquare className="w-4 h-4" /> Demander des modifications
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="gclient-modif-form">
                      <p className="text-xs font-semibold">Décrivez les modifications souhaitées :</p>
                      <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="gclient-modif-message" />
                      <input type="file" className="text-xs" onChange={(e) => setFile(e.target.files[0])} data-testid="gclient-modif-file" />
                      <div className="flex gap-2">
                        <Button onClick={() => answer("modification")} disabled={busy} className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="gclient-modif-send">Envoyer</Button>
                        <Button variant="outline" onClick={() => setMode(null)}>Annuler</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {((!c.final_ready && latest && latest.files.some((f) => f.kind === "apercu" && !f.external)) || c.unlocked) && (
              <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="gclient-downloads">
                <h3 className="font-heading font-bold text-sm mb-2">Fichiers</h3>
                <div className="space-y-2">
                  {!c.final_ready && latest && latest.files.filter((f) => f.kind === "apercu" && !f.external).map((f) => (
                    <div key={f.id} className="border border-neutral-200 rounded-xl p-3.5" data-testid="gclient-preview-dl">
                      <p className="text-sm font-medium">Prévisualisation validée — {f.name}</p>
                      <p className="text-xs text-neutral-400 mt-0.5">Fichier d'aperçu — la version définitive HD est en préparation.</p>
                      <button onClick={() => setWarnFile(f)} data-testid="gclient-preview-dl-btn"
                        className="inline-flex items-center gap-1.5 text-xs border border-neutral-300 rounded-lg px-3 py-2 mt-2 hover:border-neutral-500 transition-colors">
                        <Download className="w-3.5 h-3.5" /> Télécharger
                      </button>
                    </div>
                  ))}
                  {c.unlocked && (p.creations || []).some((x) => (x.final_files || []).some((f) => !f.external)) && (
                    <a href={`${API}/graphisme/espace/projets/${pid}/download-all`} data-testid="gclient-download-all"
                      className="inline-flex items-center gap-1.5 text-xs bg-neutral-900 text-white rounded-lg px-3 py-2 hover:bg-neutral-800">
                      <Download className="w-3.5 h-3.5" /> Télécharger tous les fichiers (ZIP)
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {warnFile && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setWarnFile(null)}
          onKeyDown={(e) => e.key === "Escape" && setWarnFile(null)} tabIndex={-1} ref={(el) => el?.focus()}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()} data-testid="gclient-warn-modal">
            <h3 className="font-heading font-bold text-lg">Attention — Fichier non finalisé</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Vous êtes sur le point de télécharger un fichier qui n'est pas encore finalisé et/ou optimisé
              pour son utilisation définitive ou pour l'impression. Le fichier final est actuellement en
              attente d'importation par le graphiste. Ce fichier peut être utilisé pour prévisualisation,
              mais nous vous recommandons d'attendre la version finale avant toute utilisation, publication
              ou impression.
            </p>
            <div className="flex flex-col gap-2">
              <a href={fileUrl(warnFile.id)} onClick={() => setWarnFile(null)} data-testid="gclient-warn-download"
                className="inline-flex items-center justify-center gap-1.5 text-sm bg-neutral-900 text-white rounded-lg px-4 py-2.5 hover:bg-neutral-800">
                <Download className="w-4 h-4" /> Télécharger quand même
              </a>
              <Button variant="outline" disabled={busy} data-testid="gclient-ask-final"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api.post(`/graphisme/espace/creations/${active}/demander-fichier-final`);
                    toast.success("Votre graphiste a été notifié — il préparera le fichier final.");
                    setWarnFile(null); load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  finally { setBusy(false); }
                }}>
                Demander le fichier final
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
